<template>
  <div class="app-config-section">
    <div class="section-header">
      <h3>小程序应用配置</h3>
    </div>

    <!-- 无应用时居中显示新增按钮 -->
    <div v-if="!appList || appList.length === 0" class="empty-state">
      <el-button type="primary" size="medium" @click="handleAddApp" icon="el-icon-plus">新增小程序应用</el-button>
    </div>

    <!-- 有应用时显示应用卡片 -->
    <div v-else class="app-config-list">
      <div
        v-for="app in appList"
        :key="app.appid"
        :class="['app-item', { 'active': selectedAppId === app.appid }]"
        @click="handleSelectApp(app.appid)"
      >
        <div class="app-item-content">
          <div class="app-name">{{ app.app_name }}</div>
          <div class="app-id">AppID: {{ app.appid }}</div>
        </div>
        <div class="app-item-actions">
          <el-button type="text" size="small" @click.stop="handleEdit(app)" class="text-green">编辑</el-button>
          <el-button
            type="text"
            size="small"
            style="color: #f56c6c"
            @click.stop="handleDeleteApp(app)"
           class="text-red">删除</el-button>
        </div>
      </div>
    </div>

    <!-- 应用配置新增/编辑弹窗 -->
    <el-drawer
      :title="appDrawerTitle"
      :visible.sync="appDrawerVisible"
      direction="rtl"
      size="600px"
      :close-on-click-modal="false"
    >
      <div class="drawer-content">
        <el-form :model="appForm" :rules="appFormRules" ref="appForm" label-width="120px">
          <el-form-item label="小程序名称" prop="app_name">
            <el-input v-model="appForm.app_name" placeholder="请输入小程序名称"></el-input>
          </el-form-item>
          <el-form-item label="小程序AppID" prop="appid">
            <el-input v-model="appForm.appid" placeholder="请输入小程序AppID" :disabled="isEditApp"></el-input>
          </el-form-item>
          <el-form-item label="小程序密钥" prop="secret_value">
            <el-input v-model="appForm.secret_value" placeholder="请输入小程序密钥" type="password"></el-input>
          </el-form-item>
          <el-form-item label="公众号AppID" prop="gzh_appid">
            <el-input v-model="appForm.gzh_appid" placeholder="请输入公众号AppID"></el-input>
          </el-form-item>
          <el-form-item label="商户号" prop="mch_id">
            <el-input v-model="appForm.mch_id" placeholder="请输入商户号"></el-input>
          </el-form-item>
          <el-form-item label="商户密钥" prop="mch_key">
            <el-input v-model="appForm.mch_key" placeholder="请输入商户密钥" type="password"></el-input>
          </el-form-item>
        </el-form>
        <div class="drawer-footer">
          <el-button @click="appDrawerVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSaveApp">保存</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  selectApi as selectAppConfigApi,
  addApi as addAppConfigApi,
  updateApi as updateAppConfigApi,
  deleteApi as deleteAppConfigApi
} from "@/services/storeConfig/storeAppConfig";

export default {
  name: "AppConfigSection",
  data() {
    return {
      appList: [],
      selectedAppId: null,
      appDrawerVisible: false,
      isEditApp: false,
      appForm: {
        app_name: "",
        appid: "",
        secret_value: "",
        gzh_appid: "",
        mch_id: "",
        mch_key: "",
        create_user: ""
      },
      appFormRules: {
        app_name: [
          { required: true, message: "请输入小程序名称", trigger: "blur" }
        ],
        appid: [
          { required: true, message: "请输入小程序AppID", trigger: "blur" }
        ],
        secret_value: [
          { required: true, message: "请输入小程序密钥", trigger: "blur" }
        ]
      }
    };
  },
  computed: {
    appDrawerTitle() {
      return this.isEditApp ? "编辑应用配置" : "新增应用配置";
    }
  },
  mounted() {
    this.loadAppList();
  },
  methods: {
    async loadAppList() {
      try {
        const res = await selectAppConfigApi({});
        if (res.code === 200) {
          this.appList = res.data || [];
          // 如果有应用，默认选中第一个
          if (this.appList.length > 0 && !this.selectedAppId) {
            this.selectedAppId = this.appList[0].appid;
            this.$emit("app-selected", this.selectedAppId);
          }
        }
      } catch (error) {
        this.$message.error("加载应用配置失败");
      }
    },

    handleSelectApp(appid) {
      this.selectedAppId = appid;
      this.$emit("app-selected", appid);
    },

    handleAddApp() {
      // 如果已有应用，不允许新增
      if (this.appList && this.appList.length > 0) {
        this.$message.warning("只允许配置一个小程序应用");
        return;
      }

      this.isEditApp = false;
      this.appForm = {
        app_name: "",
        appid: "",
        secret_value: "",
        gzh_appid: "",
        mch_id: "",
        mch_key: "",
        create_user: ""
      };
      this.appDrawerVisible = true;
      this.$nextTick(() => {
        this.$refs.appForm && this.$refs.appForm.clearValidate();
      });
    },

    handleEdit(app) {
      this.isEditApp = true;
      this.appForm = { ...app };
      this.appDrawerVisible = true;
      this.$nextTick(() => {
        this.$refs.appForm && this.$refs.appForm.clearValidate();
      });
    },

    handleSaveApp() {
      this.$refs.appForm.validate(async valid => {
        if (valid) {
          try {
            const api = this.isEditApp ? updateAppConfigApi : addAppConfigApi;
            const res = await api(this.appForm);
            if (res.code === 200) {
              this.$message.success(this.isEditApp ? "修改成功" : "新增成功");
              this.appDrawerVisible = false;
              await this.loadAppList();
              // 新增后自动选中
              if (!this.isEditApp && this.appList.length > 0) {
                this.handleSelectApp(this.appList[0].appid);
              }
            } else {
              this.$message.error(res.message || "操作失败");
            }
          } catch (error) {
            this.$message.error("操作失败");
          }
        }
      });
    },

    handleDeleteApp(app) {
      this.$confirm(
        "删除应用配置后，该应用下的所有门店将无法使用，确定要删除吗？",
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        }
      )
        .then(async () => {
          try {
            const res = await deleteAppConfigApi({ id: app.id });
            if (res.code === 200) {
              this.$message.success("删除成功");
              if (this.selectedAppId === app.appid) {
                this.selectedAppId = null;
                this.$emit("app-selected", null);
              }
              this.loadAppList();
            } else {
              this.$message.error(res.message || "删除失败");
            }
          } catch (error) {
            this.$message.error("删除失败");
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped lang="less">
.app-config-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: #fafafa;
  border-radius: 8px;
  border: 2px dashed #e6e6e6;
}

.app-config-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.app-item {
  border: 2px solid #e6e6e6;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s;
  background: #fafafa;

  &:hover {
    border-color: #65d27d;
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.2);
  }

  &.active {
    border-color: #65d27d;
    background: #fffaed;
  }

  .app-item-content {
    margin-bottom: 10px;

    .app-name {
      font-size: 16px;
      font-weight: 600;
      // color: #333;
      margin-bottom: 8px;
      color: #17a236;
    }

    .app-id {
      font-size: 13px;
      // color: #666;
      margin-bottom: 5px;
      color: #17a236;
    }
  }

  .app-item-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
}

.drawer-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-footer {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e6e6e6;
  text-align: right;
}
</style>
